import { type FormEvent, useEffect, useState } from "react";
import { ApiError, api, type PlantillaNotif } from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";

/**
 * Administra (solo admin) las plantillas de mensaje rapido del modal
 * "Pasar a un companero" (handoff). El modal las lee de la misma API.
 * Frases largas -> textarea (no input de una linea).
 */
export default function PlantillasNotifPanel() {
  const toast = useToast();
  const [items, setItems] = useState<PlantillaNotif[]>([]);
  const [loading, setLoading] = useState(true);
  const [textoNuevo, setTextoNuevo] = useState("");
  const [creando, setCreando] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editTexto, setEditTexto] = useState("");
  const [confirmDel, setConfirmDel] = useState<PlantillaNotif | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const list = await api.get<PlantillaNotif[]>(
        "/api/plantillas-notificacion?incluir_inactivas=true",
      );
      setItems(list);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function crear(e: FormEvent) {
    e.preventDefault();
    if (!textoNuevo.trim()) return;
    setCreando(true);
    try {
      await api.post<PlantillaNotif>("/api/plantillas-notificacion", {
        texto: textoNuevo.trim(),
      });
      toast.success("Plantilla creada");
      setTextoNuevo("");
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear", msg);
    } finally {
      setCreando(false);
    }
  }

  async function toggleActivo(p: PlantillaNotif) {
    try {
      await api.patch(`/api/plantillas-notificacion/${p.id}`, { activo: !p.activo });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  function startEdit(p: PlantillaNotif) {
    setEditandoId(p.id);
    setEditTexto(p.texto);
  }

  async function saveEdit(p: PlantillaNotif) {
    if (!editTexto.trim() || editTexto.trim() === p.texto) {
      setEditandoId(null);
      return;
    }
    try {
      await api.patch(`/api/plantillas-notificacion/${p.id}`, {
        texto: editTexto.trim(),
      });
      toast.success("Plantilla actualizada");
      setEditandoId(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo guardar", msg);
    }
  }

  async function eliminar(p: PlantillaNotif) {
    try {
      await api.del(`/api/plantillas-notificacion/${p.id}`);
      toast.success("Plantilla eliminada");
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  return (
    <>
      <form onSubmit={crear} className="card flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Nueva plantilla
          </label>
          <textarea
            className="input mt-1 w-full"
            rows={2}
            value={textoNuevo}
            onChange={(e) => setTextoNuevo(e.target.value)}
            placeholder="ej. El equipo pasó a segunda revisión con el proveedor — favor de gestionar el envío."
          />
        </div>
        <button
          type="submit"
          disabled={!textoNuevo.trim() || creando}
          className="btn-primary disabled:opacity-50 w-full sm:w-auto"
        >
          {creando ? "Creando..." : "+ Agregar"}
        </button>
      </form>

      <div className="card">
        <div className="text-xs text-zinc-500 mb-3">
          Mensajes rápidos reutilizables: aparecen en el botón{" "}
          <strong>📣 Notificar</strong> y en el picker{" "}
          <strong>💬 Mensaje rápido</strong> de la bitácora. En la bitácora se
          referencian por ID, así que editar aquí actualiza las notas que los usan.
        </div>
        {loading ? (
          <SkeletonRows rows={4} cols={1} />
        ) : items.length === 0 ? (
          <p className="text-sm text-zinc-500">
            No hay plantillas todavía. Agrega la primera arriba.
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {items.map((p) => (
              <li
                key={p.id}
                className={`py-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between ${p.activo ? "" : "opacity-50"}`}
              >
                {editandoId === p.id ? (
                  <div className="flex-1 space-y-2">
                    <textarea
                      autoFocus
                      className="input w-full"
                      rows={2}
                      value={editTexto}
                      onChange={(e) => setEditTexto(e.target.value)}
                    />
                    <div className="space-x-2">
                      <button onClick={() => saveEdit(p)} className="btn-primary text-xs">
                        Guardar
                      </button>
                      <button
                        onClick={() => setEditandoId(null)}
                        className="btn-ghost text-xs"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="text-zinc-200 flex-1 min-w-0 break-words whitespace-pre-wrap">{p.texto}</span>
                )}
                {editandoId !== p.id && (
                  <div className="flex flex-wrap gap-1 sm:flex-shrink-0">
                    <button onClick={() => toggleActivo(p)} className="btn-ghost text-xs">
                      {p.activo ? "Desactivar" : "Activar"}
                    </button>
                    <button onClick={() => startEdit(p)} className="btn-ghost text-xs">
                      Editar
                    </button>
                    <button
                      onClick={() => setConfirmDel(p)}
                      className="btn-ghost text-xs text-red-400 hover:text-red-300"
                    >
                      Eliminar
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title="Eliminar plantilla"
          description="Dejará de aparecer en la ventana de Notificar. Los avisos ya enviados no se modifican."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminar(confirmDel)}
        />
      )}
    </>
  );
}
